import NavBar from "../components/NavBar";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API } from '../context/AuthContext';

const ICON_PATHS = {
  target: <><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="0.5" fill="currentColor"/></>,
  trendUp: <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
  layers: <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>,
  sun: <><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></>,
  check: <><polyline points="20 6 9 17 4 12"/></>,
  flag: <><line x1="5" y1="21" x2="5" y2="3"/><path d="M5 4h13l-3 4.5L18 13H5"/></>
};

function Icon({ name, size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      {ICON_PATHS[name]}
    </svg>
  );
}

const TABS = [
  { key: 'goals', label: 'My goals', icon: 'flag' },
  { key: 'scenarios', label: 'Price scenarios', icon: 'trendUp' },
  { key: 'accumulation', label: 'Accumulation', icon: 'layers' },
  { key: 'retirement', label: 'Retirement', icon: 'sun' }
];

export default function WealthPlanner() {
  const { user, logout } = useAuth();
  const [goals, setGoals] = useState({});
  const [customScenarios, setCustomScenarios] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('goals');

  const [form, setForm] = useState({
    target_net_worth: '',
    target_year: '',
    monthly_dca: '',
    risk_tolerance: 'medium',
    retirement_monthly_income: '',
    withdrawal_rate: '4'
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [goalsRes, portfolioRes, scenariosRes] = await Promise.all([
        axios.get(`${API}/api/wealth`),
        axios.get(`${API}/api/prices/portfolio`),
        axios.get(`${API}/api/wealth/scenarios`)
      ]);
      setCustomScenarios(scenariosRes.data.scenarios || []);
      const g = goalsRes.data.goals || {};
      setGoals(g);
      setPortfolio(portfolioRes.data);
      setForm(prev => ({
        ...prev,
        target_net_worth: g.target_net_worth ? String(g.target_net_worth).replace(/[^0-9.]/g, '') : '',
        target_year: g.target_year || '',
        monthly_dca: g.monthly_dca ? String(g.monthly_dca).replace(/[^0-9.]/g, '') : '',
        risk_tolerance: g.risk_tolerance || 'medium'
      }));
    } catch (err) {
      console.error('Wealth planner load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveGoals = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/wealth`, { ...goals, ...form, target_net_worth: String(form.target_net_worth).replace(/[^0-9.]/g, ""), monthly_dca: String(form.monthly_dca).replace(/[^0-9.]/g, "") });
      setGoals({ ...goals, ...form });
      setMessage('Goals saved.');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Save goals error:', err);
    } finally {
      setSaving(false);
    }
  };
  const saveScenario = async (label, price) => {
    try {
      const res = await axios.post(`${API}/api/wealth/scenarios`, { label, price });
      setCustomScenarios(prev => [...prev, res.data.scenario].sort((a,b) => a.price - b.price));
    } catch (err) { console.error("Save scenario error:", err); }
  };
  const deleteScenario = async (id) => {
    try {
      await axios.delete(`${API}/api/wealth/scenarios/${id}`);
      setCustomScenarios(prev => prev.filter(s => s.id !== id));
    } catch (err) { console.error("Delete scenario error:", err); }
  };

  const fmt = (n) => '$' + Math.round(n || 0).toLocaleString();
  const currentYear = new Date().getFullYear();
  const targetYear = parseInt(form.target_year) || currentYear + 5;
  const yearsLeft = Math.max(1, targetYear - currentYear);
  const targetNetWorth = parseFloat(form.target_net_worth) || 0;
  const monthlyDCA = parseFloat(form.monthly_dca) || 0;
  const totalXRP = portfolio?.holdings?.filter(h => h.coin_symbol === "XRP").reduce((sum, h) => sum + parseFloat(h.amount), 0) || 0;
  const currentXRPValue = portfolio?.holdings?.filter(h => h.coin_symbol === "XRP").reduce((sum, h) => sum + (h.current_value || 0), 0) || 0;
  const totalPortfolioValue = portfolio?.summary?.total_value || 0;
  const currentPrice = currentXRPValue > 0 && totalXRP > 0 ? currentXRPValue / totalXRP : 1.37;

  const scenarios = [
    { label: 'Conservative', price: 5, color: '#9b9b9b' },
    { label: 'Base case', price: 10, color: '#1a6b4a' },
    { label: 'Optimistic', price: 25, color: '#0ea5e9' },
    { label: 'Moon', price: 100, color: '#d97706' }
  ];
  const allScenarios = [...scenarios, ...customScenarios.map(s => ({ label: s.label, price: parseFloat(s.price), color: "#8b5cf6", isCustom: true, id: s.id }))];

  const monthlyDCANeeded = (targetVal, currentVal, years) => {
    if (years <= 0) return 0;
    const gap = targetVal - currentVal;
    return Math.max(0, gap / (years * 12));
  };

  const xrpNeededAtPrice = (targetVal, price) => {
    if (price <= 0) return 0;
    return Math.ceil(targetVal / price);
  };

  const retirementTarget = (monthlyIncome, withdrawalRate) => {
    const annual = (monthlyIncome || 0) * 12;
    const rate = (withdrawalRate || 4) / 100;
    return rate > 0 ? annual / rate : 0;
  };

  if (loading) return <div style={s.loadingPage}>Loading wealth planner...</div>;

  return (
    <div style={s.page}>
      <NavBar />

      <style>{`
        .wp-tab:hover { background: rgba(0,0,0,0.05); color: #1a1a1a; }
        .wp-tab-active:hover { background: #0f2a1c !important; color: #fff !important; }
        .wp-scenario:hover { transform: translateY(-2px); box-shadow: 0 14px 28px -12px rgba(16,40,28,0.16); }
        .wp-input:focus { border-color: #4ade80 !important; box-shadow: 0 0 0 3px rgba(74,222,128,0.15); }
        .wp-btn:hover { filter: brightness(1.06); }
        * { box-sizing: border-box; }
      `}</style>
      <div style={s.content}>
        <div style={s.sectionLabel}>Wealth planning</div>
        <h1 style={s.pageTitle}>Wealth planner</h1>
        <p style={s.pageDesc}>Model your XRP wealth scenarios, set accumulation targets, and plan your financial future.</p>

        {message && <div style={s.success}><Icon name="check" size={15} color="#1a6b4a" /> {message}</div>}

        <div style={s.tabs}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={activeTab===tab.key ? 'wp-tab wp-tab-active' : 'wp-tab'}
              style={{...s.tab, ...(activeTab===tab.key ? s.tabActive : {})}}>
              <Icon name={tab.icon} size={15} /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'goals' && (
          <div style={s.section}>
            <h2 style={s.sTitle}>Set your wealth goals</h2>
            <p style={s.sDesc}>These goals power all your scenarios and planning calculations.</p>
            <div style={s.grid2}>
              <div style={s.field}>
                <label style={s.label}>Target net worth ($)</label>
                <input className="wp-input" style={s.input} type="number" inputMode="numeric" value={form.target_net_worth} onChange={e => setForm({...form, target_net_worth: e.target.value})} placeholder="1000000" />
                <p style={s.hint}>{form.target_net_worth ? Number(form.target_net_worth).toLocaleString() : "How much do you want to be worth?"}</p>
              </div>
              <div style={s.field}>
                <label style={s.label}>Target year</label>
                <input className="wp-input" style={s.input} type="number" value={form.target_year} onChange={e => setForm({...form, target_year: e.target.value})} placeholder="2030" />
                <p style={s.hint}>When do you want to reach your goal?</p>
              </div>
              <div style={s.field}>
                <label style={s.label}>Monthly DCA ($)</label>
                <input className="wp-input" style={s.input} type="number" inputMode="numeric" value={form.monthly_dca} onChange={e => setForm({...form, monthly_dca: e.target.value})} placeholder="500" />
                <p style={s.hint}>{form.monthly_dca ? "$" + Number(form.monthly_dca).toLocaleString() + "/month" : "How much do you invest each month?"}</p>
              </div>
              <div style={s.field}>
                <label style={s.label}>Risk tolerance</label>
                <select className="wp-input" style={s.input} value={form.risk_tolerance} onChange={e => setForm({...form, risk_tolerance: e.target.value})}>
                  <option value="low">Low — Conservative</option>
                  <option value="medium">Medium — Balanced</option>
                  <option value="high">High — Aggressive</option>
                </select>
              </div>
            </div>
            <button onClick={saveGoals} className="wp-btn" style={s.btn} disabled={saving}>{saving ? 'Saving...' : 'Save goals'}</button>

            {targetNetWorth > 0 && (() => {
              const atGoal = currentXRPValue >= targetNetWorth;
              const progressPct = Math.min(100, Math.round((currentXRPValue / targetNetWorth) * 100));
              return (
                <div style={s.goalSummary}>
                  <h3 style={s.goalSummaryTitle}>Your goal summary</h3>

                  <div style={s.progressTrack}>
                    <div style={{...s.progressFill, width: `${progressPct}%`}} />
                  </div>
                  <div style={s.progressLabel}>{progressPct}% of the way to {fmt(targetNetWorth)}</div>

                  <div style={s.goalGrid}>
                    <div style={s.goalCard}>
                      <div style={s.goalLabel}>Target</div>
                      <div style={s.goalValue}>{fmt(targetNetWorth)}</div>
                      <div style={s.goalSub}>by {targetYear}</div>
                    </div>
                    <div style={s.goalCard}>
                      <div style={s.goalLabel}>Years remaining</div>
                      <div style={s.goalValue}>{yearsLeft}</div>
                      <div style={s.goalSub}>years to go</div>
                    </div>
                    <div style={s.goalCard}>
                      <div style={s.goalLabel}>Current XRP value</div>
                      <div style={s.goalValue}>{fmt(currentXRPValue)}</div>
                      <div style={s.goalSub}>{totalXRP.toLocaleString()} XRP</div>
                    </div>
                    <div style={{...s.goalCardHero, ...(atGoal ? s.goalCardHeroWin : s.goalCardHeroGap)}}>
                      <div style={{...s.goalLabel, color: atGoal ? 'rgba(255,255,255,0.65)' : '#92400e'}}>Gap to target</div>
                      <div style={{...s.goalValueHero, color: atGoal ? '#fff' : '#92400e'}}>
                        {atGoal ? 'Goal reached' : fmt(targetNetWorth - currentXRPValue)}
                      </div>
                      <div style={{...s.goalSub, color: atGoal ? 'rgba(255,255,255,0.5)' : '#92400e'}}>at current prices</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'scenarios' && (
          <div style={s.section}>
            <h2 style={s.sTitle}>Price scenarios</h2>
            <p style={s.sDesc}>What your {totalXRP.toLocaleString()} XRP is worth at different price targets.</p>

            <div style={s.chartCard}>
              <div style={s.chartTitle}>Portfolio value by scenario</div>
              {scenarios.map(sc => {
                const value = totalXRP * sc.price;
                const maxVal = Math.max(...scenarios.map(x => totalXRP * x.price), 1);
                const pct = Math.max(2, Math.round((value / maxVal) * 100));
                return (
                  <div key={sc.label} style={s.chartRow}>
                    <div style={s.chartRowLabel}>{sc.label}</div>
                    <div style={s.chartBarTrack}>
                      <div style={{...s.chartBarFill, width: `${pct}%`, background: sc.color}} />
                    </div>
                    <div style={s.chartRowValue}>{fmt(value)}</div>
                  </div>
                );
              })}
            </div>

            <div style={s.scenarioGrid}>
              {scenarios.map(sc => {
                const value = totalXRP * sc.price;
                const gain = value - currentXRPValue;
                const gainPct = currentXRPValue > 0 ? (gain / currentXRPValue) * 100 : 0;
                const hitsTarget = targetNetWorth > 0 && value >= targetNetWorth;
                return (
                  <div key={sc.label} className="wp-scenario" style={{...s.scenarioCard, borderTop: `3px solid ${sc.color}`}}>
                    <div style={s.scenarioLabel}>{sc.label}</div>
                    <div style={{...s.scenarioPrice, color: sc.color}}>${sc.price} XRP</div>
                    <div style={s.scenarioDivider} />
                    <div style={s.scenarioRow}>
                      <span style={s.scenarioKey}>Portfolio value</span>
                      <span style={s.scenarioVal}>{fmt(value)}</span>
                    </div>
                    <div style={s.scenarioRow}>
                      <span style={s.scenarioKey}>Gain from today</span>
                      <span style={{...s.scenarioVal, color: '#1a6b4a'}}>{fmt(gain)} (+{Math.round(gainPct)}%)</span>
                    </div>
                    {targetNetWorth > 0 && (
                      <div style={{...s.badgePill, ...(hitsTarget ? s.badgeWin : s.badgeWarn)}}>
                        {hitsTarget ? <><Icon name="check" size={12} /> Hits your {fmt(targetNetWorth)} goal</> : `${fmt(targetNetWorth - value)} short of goal`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {customScenarios.length > 0 && (
              <div style={{marginBottom:"1.5rem"}}>
                <h3 style={s.subTitle}>Saved scenarios</h3>
                <div style={s.scenarioGrid}>
                  {customScenarios.map(sc => {
                    const value = totalXRP * parseFloat(sc.price);
                    const gain = value - currentXRPValue;
                    const gainPct = currentXRPValue > 0 ? (gain / currentXRPValue) * 100 : 0;
                    const hitsTarget = targetNetWorth > 0 && value >= targetNetWorth;
                    return (
                      <div key={sc.id} className="wp-scenario" style={{...s.scenarioCard, borderTop:"3px solid #8b5cf6", position:"relative"}}>
                        <button onClick={() => deleteScenario(sc.id)} style={s.deleteBtn}>✕</button>
                        <div style={s.scenarioLabel}>{sc.label}</div>
                        <div style={{...s.scenarioPrice, color:"#8b5cf6"}}>${parseFloat(sc.price).toLocaleString()} XRP</div>
                        <div style={s.scenarioDivider} />
                        <div style={s.scenarioRow}><span style={s.scenarioKey}>Portfolio value</span><span style={s.scenarioVal}>{fmt(value)}</span></div>
                        <div style={s.scenarioRow}><span style={s.scenarioKey}>Gain</span><span style={{...s.scenarioVal, color:"#1a6b4a"}}>{fmt(gain)} (+{Math.round(gainPct)}%)</span></div>
                        {targetNetWorth > 0 && <div style={{...s.badgePill, ...(hitsTarget ? s.badgeWin : s.badgeWarn)}}>{hitsTarget ? <><Icon name="check" size={12} /> Hits goal</> : fmt(targetNetWorth - value) + " short"}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={s.customSection}>
              <h3 style={s.subTitle}>Custom scenario</h3>
              <p style={s.sDesc}>Enter any XRP price to see your outcome.</p>
              <CustomScenario totalXRP={totalXRP} currentXRPValue={currentXRPValue} targetNetWorth={targetNetWorth} fmt={fmt} onSave={saveScenario} />
            </div>
          </div>
        )}

        {activeTab === 'accumulation' && (
          <div style={s.section}>
            <h2 style={s.sTitle}>Accumulation planner</h2>
            <p style={s.sDesc}>How much XRP do you need and how to get there?</p>

            {targetNetWorth === 0 ? (
              <div style={s.emptyState}>
                <p>Set your target net worth in <button onClick={() => setActiveTab('goals')} style={s.inlineBtn}>My goals</button> to see accumulation targets.</p>
              </div>
            ) : (
              <div>
                <div style={s.accumGrid}>
                  {allScenarios.map(sc => {
                    const xrpNeeded = xrpNeededAtPrice(targetNetWorth, sc.price);
                    const xrpShort = Math.max(0, xrpNeeded - totalXRP);
                    const costToAccumulate = xrpShort * currentPrice;
                    const monthlyNeeded = monthlyDCANeeded(costToAccumulate, 0, yearsLeft);
                    const alreadyThere = totalXRP >= xrpNeeded;
                    const havePct = Math.min(100, Math.round((totalXRP / Math.max(1, xrpNeeded)) * 100));
                    return (
                      <div key={sc.label} style={{...s.accumCard, borderLeft: `3px solid ${sc.color}`}}>
                        <div style={{...s.accumLabel, color: sc.color}}>{sc.label} — ${sc.price} XRP</div>

                        <div style={s.accumProgressTrack}>
                          <div style={{...s.accumProgressFill, width: `${havePct}%`, background: alreadyThere ? '#1a6b4a' : sc.color}} />
                        </div>

                        <div style={s.accumMain}>
                          <div style={s.accumStat}>
                            <div style={s.accumStatLabel}>XRP needed</div>
                            <div style={s.accumStatValue}>{xrpNeeded.toLocaleString()}</div>
                          </div>
                          <div style={s.accumStat}>
                            <div style={s.accumStatLabel}>You have</div>
                            <div style={{...s.accumStatValue, color: alreadyThere ? '#1a6b4a' : '#1a1a1a'}}>{Math.round(totalXRP).toLocaleString()}</div>
                          </div>
                          <div style={s.accumStat}>
                            <div style={s.accumStatLabel}>Still need</div>
                            <div style={{...s.accumStatValue, color: alreadyThere ? '#1a6b4a' : '#92400e'}}>{alreadyThere ? 'Done' : xrpShort.toLocaleString()}</div>
                          </div>
                        </div>
                        {!alreadyThere && (
                          <div style={s.accumDCA}>
                            To accumulate {xrpShort.toLocaleString()} more XRP by {targetYear} at today's price:<br/>
                            <strong style={{color: sc.color}}>{fmt(monthlyNeeded)}/month needed</strong>
                            {monthlyDCA > 0 && <span style={{color: monthlyDCA >= monthlyNeeded ? '#1a6b4a' : '#dc2626'}}> · you're DCAing {fmt(monthlyDCA)}/mo {monthlyDCA >= monthlyNeeded ? '✓ on track' : '— increase by ' + fmt(monthlyNeeded - monthlyDCA)}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {monthlyDCA > 0 && (
                  <div style={s.dcaProjection}>
                    <h3 style={s.subTitle}>DCA projection</h3>
                    <p style={s.sDesc}>If you DCA {fmt(monthlyDCA)}/month at today's price of ${currentPrice.toFixed(2)}, you'll accumulate approximately <strong>{Math.round((monthlyDCA / currentPrice) * yearsLeft * 12).toLocaleString()} more XRP</strong> by {targetYear}, bringing your total to approximately <strong>{Math.round(totalXRP + (monthlyDCA / currentPrice) * yearsLeft * 12).toLocaleString()} XRP</strong>.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'retirement' && (
          <div style={s.section}>
            <h2 style={s.sTitle}>Retirement calculator</h2>
            <p style={s.sDesc}>Can your XRP fund your retirement? Based on the 4% safe withdrawal rule.</p>

            <div style={s.grid2}>
              <div style={s.field}>
                <label style={s.label}>Monthly income needed ($)</label>
                <input className="wp-input" style={s.input} type="number" value={form.retirement_monthly_income} onChange={e => setForm({...form, retirement_monthly_income: e.target.value})} placeholder="5000" />
                <p style={s.hint}>How much do you need per month to retire?</p>
              </div>
              <div style={s.field}>
                <label style={s.label}>Withdrawal rate (%)</label>
                <select className="wp-input" style={s.input} value={form.withdrawal_rate} onChange={e => setForm({...form, withdrawal_rate: e.target.value})}>
                  <option value="3">3% — very conservative</option>
                  <option value="4">4% — standard safe withdrawal</option>
                  <option value="5">5% — moderate</option>
                </select>
                <p style={s.hint}>% of portfolio withdrawn annually</p>
              </div>
            </div>

            {form.retirement_monthly_income > 0 && (
              <div>
                <div style={s.retirementHero}>
                  <div style={s.retirementLabel}>Portfolio needed to retire</div>
                  <div style={s.retirementValue}>{fmt(retirementTarget(form.retirement_monthly_income, form.withdrawal_rate))}</div>
                  <div style={s.retirementSub}>To generate {fmt(form.retirement_monthly_income)}/month at {form.withdrawal_rate}% withdrawal rate</div>
                </div>

                <div style={s.retireGrid}>
                  {allScenarios.map(sc => {
                    const portfolioValue = totalPortfolioValue + (totalXRP * sc.price - currentXRPValue);
                    const monthlyIncome = portfolioValue * ((parseFloat(form.withdrawal_rate) || 4) / 100) / 12;
                    const canRetire = monthlyIncome >= form.retirement_monthly_income;
                    return (
                      <div key={sc.label} className="wp-scenario" style={{...s.scenarioCard, borderTop: `3px solid ${sc.color}`}}>
                        <div style={{...s.scenarioLabel, color: sc.color}}>{sc.label}</div>
                        <div style={{...s.scenarioPrice, color: sc.color, fontSize:'18px'}}>${sc.price} XRP</div>
                        <div style={s.scenarioDivider} />
                        <div style={s.scenarioRow}>
                          <span style={s.scenarioKey}>Portfolio value</span>
                          <span style={s.scenarioVal}>{fmt(portfolioValue)}</span>
                        </div>
                        <div style={s.scenarioRow}>
                          <span style={s.scenarioKey}>Monthly income</span>
                          <span style={{...s.scenarioVal, color: canRetire ? '#1a6b4a' : '#1a1a1a'}}>{fmt(monthlyIncome)}/mo</span>
                        </div>
                        <div style={{...s.badgePill, ...(canRetire ? s.badgeWin : s.badgeWarn)}}>
                          {canRetire ? <><Icon name="check" size={12} /> You can retire!</> : `Need ${fmt(form.retirement_monthly_income - monthlyIncome)} more/mo`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!form.retirement_monthly_income && (
              <div style={s.emptyState}>
                <p>Enter your monthly income needed above to see retirement scenarios.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CustomScenario({ totalXRP, currentXRPValue, targetNetWorth, fmt, onSave }) {
  const [price, setPrice] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const value = totalXRP * (parseFloat(price) || 0);
  const gain = value - currentXRPValue;
  const gainPct = currentXRPValue > 0 ? (gain / currentXRPValue) * 100 : 0;

  const handleSave = async () => {
    if (!price || !label) return;
    setSaving(true);
    try {
      await onSave(label, parseFloat(price));
      setPrice("");
      setLabel("");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{display:"flex", gap:"12px", flexWrap:"wrap", marginBottom:"1rem"}}>
        <div style={{flex:"1", minWidth:"160px"}}>
          <label style={s.label}>XRP price ($)</label>
          <input className="wp-input" style={{...s.input, width:"100%", boxSizing:"border-box"}} type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="e.g. 50" />
        </div>
        <div style={{flex:"1", minWidth:"160px"}}>
          <label style={s.label}>Scenario name</label>
          <input className="wp-input" style={{...s.input, width:"100%", boxSizing:"border-box"}} type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. My target" />
        </div>
      </div>
      {price && (
        <div style={{display:"flex", gap:"12px", flexWrap:"wrap", marginBottom:"1rem"}}>
          <div style={s.customChip}>
            <div style={s.customChipLabel}>Value</div>
            <div style={s.customChipValue}>{fmt(value)}</div>
          </div>
          <div style={{...s.customChip, background:"rgba(74,222,128,0.12)"}}>
            <div style={s.customChipLabel}>Gain</div>
            <div style={{...s.customChipValue, color:"#1a6b4a"}}>{fmt(gain)} (+{Math.round(gainPct)}%)</div>
          </div>
          {targetNetWorth > 0 && (
            <div style={{...s.customChip, background: value >= targetNetWorth ? "rgba(74,222,128,0.12)" : "#fef3c7"}}>
              <div style={s.customChipLabel}>vs Goal</div>
              <div style={{...s.customChipValue, fontSize:"15px", color: value >= targetNetWorth ? "#1a6b4a" : "#92400e"}}>{value >= targetNetWorth ? "Goal hit!" : fmt(targetNetWorth - value) + " short"}</div>
            </div>
          )}
        </div>
      )}
      {price && label && (
        <button onClick={handleSave} disabled={saving} className="wp-btn" style={s.btn}>
          {saving ? "Saving..." : "Save scenario"}
        </button>
      )}
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', background:'#fafaf8', fontFamily:"'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif" },
  loadingPage: { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#fafaf8', color:'#6b6b6b', fontFamily:"'Inter', system-ui, sans-serif" },
  content: { maxWidth:'1080px', margin:'0 auto', padding:'3rem 2rem' },
  sectionLabel: { fontSize:'12px', fontWeight:'600', letterSpacing:'0.08em', textTransform:'uppercase', color:'#1a6b4a', marginBottom:'0.6rem' },
  pageTitle: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'32px', fontWeight:'400', color:'#1a1a1a', marginBottom:'0.5rem', letterSpacing:'-0.01em' },
  pageDesc: { fontSize:'15px', color:'#6b6b6b', marginBottom:'2rem', lineHeight:'1.6', maxWidth:'640px' },
  success: { display:'flex', alignItems:'center', gap:'8px', background:'rgba(74,222,128,0.12)', border:'1px solid rgba(26,107,74,0.2)', color:'#1a6b4a', padding:'10px 14px', borderRadius:'8px', fontSize:'14px', fontWeight:'600', marginBottom:'1rem' },
  tabs: { display:'flex', gap:'4px', marginBottom:'2rem', background:'#fff', padding:'5px', borderRadius:'12px', border:'1px solid rgba(0,0,0,0.07)', boxShadow:'0 2px 6px rgba(16,40,28,0.05)', width:'fit-content', flexWrap:'wrap' },
  tab: { display:'flex', alignItems:'center', gap:'7px', padding:'9px 16px', borderRadius:'9px', border:'none', background:'transparent', fontSize:'13px', fontWeight:'600', color:'#6b6b6b', cursor:'pointer', fontFamily:"'Inter', system-ui, sans-serif", transition:'all 0.15s ease' },
  tabActive: { background:'#0f2a1c', color:'#fff' },
  section: { background:'#fff', borderRadius:'18px', padding:'2rem', border:'1px solid rgba(0,0,0,0.07)', boxShadow:'0 2px 6px rgba(16,40,28,0.06)' },
  sTitle: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'22px', fontWeight:'500', color:'#1a1a1a', marginBottom:'0.4rem' },
  sDesc: { fontSize:'14px', color:'#6b6b6b', marginBottom:'1.75rem', lineHeight:'1.6' },
  subTitle: { fontSize:'15px', fontWeight:'700', color:'#1a1a1a', marginBottom:'1rem' },
  grid2: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:'16px', marginBottom:'1.75rem' },
  field: { display:'flex', flexDirection:'column' },
  label: { fontSize:'11px', fontWeight:'600', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'7px' },
  input: { padding:'11px 14px', border:'1px solid rgba(0,0,0,0.12)', borderRadius:'8px', fontSize:'14px', color:'#1a1a1a', outline:'none', fontFamily:"'Inter', system-ui, sans-serif", background:'#fff' },
  hint: { fontSize:'11px', color:'#9b9b9b', marginTop:'5px' },
  btn: { padding:'11px 26px', background:'#1a6b4a', color:'#fff', border:'none', borderRadius:'9px', fontSize:'14px', fontWeight:'700', cursor:'pointer', fontFamily:"'Inter', system-ui, sans-serif", boxShadow:'0 6px 16px rgba(26,107,74,0.22)' },

  goalSummary: { marginTop:'2.25rem', paddingTop:'1.75rem', borderTop:'1px solid rgba(0,0,0,0.07)' },
  goalSummaryTitle: { fontSize:'15px', fontWeight:'700', color:'#1a1a1a', marginBottom:'1rem' },
  progressTrack: { height:'10px', background:'#f4f1eb', borderRadius:'99px', overflow:'hidden', marginBottom:'8px' },
  progressFill: { height:'100%', borderRadius:'99px', background:'linear-gradient(90deg, #1a6b4a, #4ade80)', transition:'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)' },
  progressLabel: { fontSize:'12px', color:'#6b6b6b', marginBottom:'1.5rem' },
  goalGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:'12px' },
  goalCard: { background:'#f4f1eb', borderRadius:'12px', padding:'1.1rem' },
  goalCardHero: { borderRadius:'12px', padding:'1.1rem' },
  goalCardHeroGap: { background:'#fef3c7' },
  goalCardHeroWin: { background:'linear-gradient(135deg, #0f2a1c 0%, #06100b 100%)', boxShadow:'0 10px 24px -10px rgba(5,20,12,0.4)' },
  goalLabel: { fontSize:'11px', fontWeight:'600', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' },
  goalValue: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'22px', fontWeight:'600', color:'#1a1a1a' },
  goalValueHero: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'22px', fontWeight:'600' },
  goalSub: { fontSize:'11px', color:'#9b9b9b', marginTop:'3px' },

  chartCard: { background:'#fff', borderRadius:'16px', padding:'1.75rem', border:'1px solid rgba(0,0,0,0.07)', boxShadow:'0 2px 6px rgba(16,40,28,0.06)', marginBottom:'1.5rem' },
  chartTitle: { fontSize:'14px', fontWeight:'700', color:'#1a1a1a', marginBottom:'1.5rem' },
  chartRow: { display:'grid', gridTemplateColumns:'110px 1fr 110px', alignItems:'center', gap:'16px', marginBottom:'1.1rem' },
  chartRowLabel: { fontSize:'13px', fontWeight:'600', color:'#1a1a1a', whiteSpace:'nowrap' },
  chartBarTrack: { height:'10px', background:'#f4f1eb', borderRadius:'99px', overflow:'hidden' },
  chartBarFill: { height:'100%', borderRadius:'99px', transition:'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)' },
  chartRowValue: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'15px', fontWeight:'700', color:'#1a1a1a', textAlign:'right' },

  scenarioGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:'16px', marginBottom:'2rem' },
  scenarioCard: { position:'relative', background:'#fff', border:'1px solid rgba(0,0,0,0.07)', borderRadius:'14px', padding:'1.4rem', boxShadow:'0 2px 6px rgba(16,40,28,0.05)' },
  scenarioLabel: { fontSize:'12px', fontWeight:'600', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'0.5rem' },
  scenarioPrice: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'26px', fontWeight:'600', marginBottom:'0.75rem' },
  scenarioDivider: { height:'1px', background:'rgba(0,0,0,0.07)', marginBottom:'0.75rem' },
  scenarioRow: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' },
  scenarioKey: { fontSize:"12px", color:"#6b6b6b", flexShrink: 0, marginRight: "1rem" },
  scenarioVal: { fontSize:"13px", fontWeight:"700", color:"#1a1a1a", textAlign:"right" },
  badgePill: { display:'flex', alignItems:'center', justifyContent:'center', gap:'5px', fontSize:'12px', padding:'7px 10px', borderRadius:'8px', marginTop:'0.75rem', fontWeight:'700', textAlign:'center' },
  badgeWin: { background:'rgba(74,222,128,0.14)', color:'#1a6b4a' },
  badgeWarn: { background:'#fef3c7', color:'#92400e' },
  deleteBtn: { position:"absolute", top:"10px", right:"10px", background:"transparent", border:"none", color:"#dc2626", cursor:"pointer", fontSize:"14px" },
  customSection: { paddingTop:'1.75rem', borderTop:'1px solid rgba(0,0,0,0.07)' },
  customChip: { background:"#f4f1eb", borderRadius:"10px", padding:"10px 16px", textAlign:"center" },
  customChipLabel: { fontSize:"11px", color:"#6b6b6b", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"4px" },
  customChipValue: { fontFamily:"'Fraunces', Georgia, serif", fontSize:"18px", fontWeight:"700", color:"#1a1a1a" },

  accumGrid: { display:'flex', flexDirection:'column', gap:'14px', marginBottom:'1.75rem' },
  accumCard: { background:'#f9f9f7', borderRadius:'12px', padding:'1.4rem' },
  accumLabel: { fontSize:'13px', fontWeight:'700', marginBottom:'0.75rem' },
  accumProgressTrack: { height:'7px', background:'rgba(0,0,0,0.07)', borderRadius:'99px', overflow:'hidden', marginBottom:'0.9rem' },
  accumProgressFill: { height:'100%', borderRadius:'99px', transition:'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)' },
  accumMain: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'12px', marginBottom:'0.75rem' },
  accumStat: { background:'#fff', borderRadius:'9px', padding:'0.8rem' },
  accumStatLabel: { fontSize:'11px', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'4px' },
  accumStatValue: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'19px', fontWeight:'600', color:'#1a1a1a' },
  accumDCA: { fontSize:'13px', color:'#6b6b6b', lineHeight:'1.6', background:'#fff', borderRadius:'9px', padding:'0.8rem' },
  dcaProjection: { background:'rgba(74,222,128,0.1)', borderRadius:'14px', padding:'1.4rem', marginTop:'1rem' },

  retirementHero: { background:'linear-gradient(135deg, #0f2a1c 0%, #06100b 100%)', borderRadius:'16px', padding:'2rem', textAlign:'center', marginBottom:'1.75rem', boxShadow:'0 20px 40px -16px rgba(5,20,12,0.4)' },
  retirementLabel: { fontSize:'12px', fontWeight:'600', color:'#4ade80', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'10px' },
  retirementValue: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'42px', fontWeight:'600', color:'#fff', marginBottom:'6px' },
  retirementSub: { fontSize:'13px', color:'rgba(255,255,255,0.55)' },
  retireGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(190px, 1fr))', gap:'16px' },
  emptyState: { background:'#f4f1eb', borderRadius:'14px', padding:'2.5rem', textAlign:'center', fontSize:'14px', color:'#6b6b6b' },
  inlineBtn: { background:'none', border:'none', color:'#1a6b4a', cursor:'pointer', fontWeight:'700', fontSize:'14px', padding:'0', textDecoration:'underline' }
};
